import { useCallback, useMemo } from 'react';

import useFetch from 'hooks/useFetch';

import useTranslation from './hooks/useTranslation';

import {
    CHEMICALS_API,
    CHEMICAL_DB_API_HOST,
    HTTP_STATUS_BAD_GATEWAY,
    HTTP_STATUS_FORBIDDEN,
    HTTP_STATUS_NOT_FOUND,
    HTTP_STATUS_RATE_LIMITED,
    JSON_HEADERS
} from './constants';
import { describeError } from './helpers/describeError';
import type {
    ClearFormulationVerification,
    EnrichedChemical,
    FetchFormulationReview,
    FormulationReview,
    FormulationVerification,
    SaveFormulationVerification
} from './types';

// Errors surface inline in the review modal rather than through the retry toast:
// retrying a 403 or a rate-limited request immediately cannot succeed.
const useFormulationReview = () => {
    const { authenticatedDelete, authenticatedGet, authenticatedPut } = useFetch({
        apiHost: CHEMICAL_DB_API_HOST,
        headers: JSON_HEADERS
    });
    const { AGRIAN_UNAVAILABLE, FORBIDDEN, NOT_FOUND, RATE_LIMITED, UNAVAILABLE } =
        useTranslation();

    const messages = useMemo(
        () => ({
            byStatus: {
                [HTTP_STATUS_FORBIDDEN]: FORBIDDEN,
                [HTTP_STATUS_NOT_FOUND]: NOT_FOUND,
                [HTTP_STATUS_RATE_LIMITED]: RATE_LIMITED
            },
            fallback: UNAVAILABLE
        }),
        [FORBIDDEN, NOT_FOUND, RATE_LIMITED, UNAVAILABLE]
    );

    // Only DELETE re-derives from Agrian, so only it can mean "provider down, nothing changed".
    const clearMessages = useMemo(
        () => ({
            byStatus: { ...messages.byStatus, [HTTP_STATUS_BAD_GATEWAY]: AGRIAN_UNAVAILABLE },
            fallback: UNAVAILABLE
        }),
        [messages, AGRIAN_UNAVAILABLE, UNAVAILABLE]
    );

    const fetchFormulationReview: FetchFormulationReview = useCallback(
        async ({ id }) => {
            try {
                const { data, error } = await authenticatedGet<FormulationReview>(
                    `${CHEMICALS_API}/${id}/formulation-review`
                );

                return error || !data
                    ? { data: null, errorMessage: describeError(error, messages) }
                    : { data, errorMessage: null };
            } catch (error) {
                // useFetch does not catch a rejected token fetch; without this the modal
                // would sit on the loading state with no way back.
                console.error('Formulation review fetch failed', error);

                return { data: null, errorMessage: messages.fallback };
            }
        },
        [authenticatedGet, messages]
    );

    const saveFormulationVerification: SaveFormulationVerification = useCallback(
        async ({ id, request }) => {
            try {
                const { data, error } = await authenticatedPut<FormulationVerification>(
                    `${CHEMICALS_API}/${id}/formulation-verification`,
                    request
                );

                return error || !data
                    ? { data: null, errorMessage: describeError(error, messages) }
                    : { data, errorMessage: null };
            } catch (error) {
                console.error('Formulation verification save failed', error);

                return { data: null, errorMessage: messages.fallback };
            }
        },
        [authenticatedPut, messages]
    );

    const clearFormulationVerification: ClearFormulationVerification = useCallback(
        async ({ id }) => {
            try {
                const { data, error } = await authenticatedDelete<EnrichedChemical>(
                    `${CHEMICALS_API}/${id}/formulation-verification`
                );

                return error || !data
                    ? { data: null, errorMessage: describeError(error, clearMessages) }
                    : { data, errorMessage: null };
            } catch (error) {
                console.error('Formulation verification clear failed', error);

                return { data: null, errorMessage: clearMessages.fallback };
            }
        },
        [authenticatedDelete, clearMessages]
    );

    return { clearFormulationVerification, fetchFormulationReview, saveFormulationVerification };
};

export default useFormulationReview;
